<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeChevronUp from "@dsk/icons/LeChevronUp.vue";
import { h, ref } from "vue";

import type { DictsysContextExample } from "@/types";

import LeArticleHoverableArea from "./LeArticleHoverableArea.vue";

const props = defineProps<{
  contextExamples: DictsysContextExample[];
  targetLanguage: string;
}>();

const isOpen = ref<boolean>(false);

const HighlightedContextExample = (contextExample: DictsysContextExample) => {
  const words = contextExample.example.split(/(\w+|\W+)/).filter((element) => element !== "");

  return words.map((word) => {
    if (word === contextExample.word) {
      return h("span", { class: "le-article-context-examples__highlight" }, word);
    }

    return word;
  });
};
</script>

<template>
  <div class="le-article-context-examples">
    <div v-if="isOpen">
      <LeHeading tag="h4" class="le-article-context-examples__heading" lang="da">Konteksteksempler</LeHeading>

      <ul class="le-article-context-examples__list" :lang="props.targetLanguage">
        <li
          v-for="(contextExample, contextExampleIndex) in props.contextExamples"
          :key="contextExampleIndex"
          class="le-article-context-examples__list-element"
          :class="{
            'le-article-context-examples__list-element--last': contextExampleIndex === props.contextExamples.length - 1
          }"
        >
          <LeArticleHoverableArea class="le-article-context-examples__example"
            ><HighlightedContextExample :example="contextExample.example" :word="contextExample.word"
          /></LeArticleHoverableArea>
        </li>
      </ul>
    </div>

    <LeButton
      variant="outline"
      :class="{ 'le-article-context-examples__button--active': isOpen }"
      class="le-article-context-examples__button"
      @click="isOpen = !isOpen"
    >
      Konteksteksempler
      <LeChevronUp
        class="le-article-context-examples__button-icon"
        :class="{ 'le-article-context-examples__button-icon--active': isOpen }"
      />
    </LeButton>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-article-context-examples {
  &__heading {
    margin-bottom: 16px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
    padding-left: 15px;
    color: @colors[@secondary][800];
    font-size: 17px;
  }

  &__list-element {
    margin-bottom: 16px;

    #media-breakpoint-up(md, {
      margin-bottom: 20px;
    });

    &--last {
      margin-bottom: 0;
    }
  }

  &__example {
    color: @colors[@neutrals][500];
    .dictsys-article-text();
  }

  ::v-deep(&__highlight) {
    color: @colors[@primary][700];
    .dictsys-article-text--bold();
  }

  &__button--active {
    margin-top: 28px;
  }

  &__button-icon {
    transform: rotate(180deg);

    &--active {
      transform: none;
    }
  }
}
</style>
